import _ from 'lodash';

export const computeHeaderColumns = (
	showPipelineColumn: boolean,
	maxDepth: number,
	hasCompanyGroupCardFeature: boolean,
	hasSubaccountsV2: boolean,
	beginMerge: boolean,
	windowWidth: number
): string[] => {
	const showPipelineColumnWithBiggerWidth = showPipelineColumn && windowWidth > 1500;
	const statusColumnWidth = beginMerge ? '250px' : '150px';
	const isSubaccount = hasCompanyGroupCardFeature || hasSubaccountsV2;
	const numberOfColumns = isSubaccount
		? showPipelineColumnWithBiggerWidth
			? 5
			: 4
		: showPipelineColumnWithBiggerWidth
		? 6
		: 5;

	const getColumnWidth = (index: number): string | null => {
		if (index === 0) return '1fr';

		if (isSubaccount) {
			if (index === maxDepth + 1 || index === maxDepth + 2) return '180px';
			if (showPipelineColumnWithBiggerWidth && index === maxDepth + 3) return '180px';
			if (index === maxDepth + (showPipelineColumnWithBiggerWidth ? 4 : 3)) return '220px';
		} else {
			if (index === maxDepth + 1) return '25%';
			if (index === maxDepth + 2) return '15%';
			if (index === maxDepth + 3) return '15%';
			if (showPipelineColumnWithBiggerWidth && index === maxDepth + 4) return '10%';
			if (index === maxDepth + (showPipelineColumnWithBiggerWidth ? 5 : 4)) return statusColumnWidth;
		}

		return null;
	};

	const columns = _.times(maxDepth + numberOfColumns, index => getColumnWidth(index)).filter(
		(width): width is string => width !== null
	);

	return columns;
};

export function calculateTotalVisibleRows(
	flatTree: any[],
	rowState: Record<string, { status: string; subaccounts: any[] }>
) {
	let totalRows = 0;
	const processedSubaccounts = new Set();

	flatTree.forEach(node => {
		if (node.hidden || processedSubaccounts.has(node.id)) return;

		totalRows++;
		const rowStateForNode = rowState[node.id] || { status: 'CLOSED', subaccounts: [] };
		if (rowStateForNode.status === 'OPEN' && rowStateForNode.subaccounts?.length) {
			totalRows += rowStateForNode.subaccounts.filter(subaccount => !subaccount.hidden).length;
			rowStateForNode.subaccounts.forEach(subaccount => {
				processedSubaccounts.add(subaccount.id);
			});
		}
	});
	return totalRows;
}

export function calculateSubaccountHeight(node: any, rowState: any): number {
	const rowStateForNode = rowState[node.id] || { status: 'CLOSED', subaccounts: [] };
	if (rowStateForNode.status !== 'OPEN') return 0;

	let totalHeight = 0;
	for (const sub of rowStateForNode.subaccounts || []) {
		if (sub.hidden) continue;
		totalHeight += 64;
		totalHeight += calculateSubaccountHeight(sub, rowState); // rekursivt!
	}

	return totalHeight;
}

export interface Field {
	type?: string;
	field?: string;
	width?: string;
	className?: string;
	iconOnly?: boolean;
	existing?: boolean;
	path?: string;
	skipAdditionalInfo?: boolean;
	header?: string;
}

export function generatePreColumns(node: any): Field[] {
	if (!node.preColumns?.length) return [];

	return node.preColumns.map((type: string) => ({
		type,
		width: '4%'
	}));
}

export function generateTreeStructureFields(
	level: number,
	maxDepth: number,
	isSubaccount: boolean,
	isLastChild: boolean,
	holes: number[]
): Field[] {
	const fields: Field[] = [];
	const nameColumn = 'nameSoliditet';

	for (let j = 0; j <= maxDepth; j++) {
		if (j === level) {
			fields.push({
				field: 'name',
				type: nameColumn,
				skipAdditionalInfo: true,
				header: 'default.account',
				width: 100 - (maxDepth * 4 + 10) + '%',
				className: 'overflow-visible'
			});
		}

		if (isSubaccount) {
			if (j === level - 1) {
				fields.push({
					type: isLastChild ? 'parentLineLast' : 'parentLine',
					width: '4%'
				});
			} else if (j < level) {
				fields.push({
					type: 'padding',
					width: '4%'
				});
			}
		}
	}

	return fields;
}

export function generateAccountFields(
	node: any,
	isSubaccount: boolean,
	showPipelineColumnWithBiggerWidth: boolean,
	hasCompanyGroupCardFeature: boolean,
	hasSubaccountsV2: boolean
): Field[] {
	const fields: Field[] = [];
	const existing = node.existing;
	const external = node.matchInUpsales?.isExternal || false;

	if ((existing && !external) || isSubaccount) {
		if (!hasCompanyGroupCardFeature && !hasSubaccountsV2) {
			fields.push({
				type: 'history',
				width: '20%'
			});
		}
		fields.push({
			field: 'accountManager',
			type: 'accountManager',
			className: 'accountManager',
			iconOnly: true,
			existing: existing,
			width: '10%'
		});

		fields.push({
			type: 'currency',
			path: 'growth.salesLast12Months',
			width: '10%'
		});
		if (showPipelineColumnWithBiggerWidth) {
			fields.push({
				type: 'currency',
				path: 'totalPipeline',
				width: '10%'
			});
		}
	} else {
		fields.push({}, {});
		if (showPipelineColumnWithBiggerWidth) {
			fields.push({});
		}
		if (!hasCompanyGroupCardFeature && !hasSubaccountsV2) {
			fields.push({});
		}
	}

	return fields;
}

export function generateActionFields(
	node: any,
	isSubaccount: boolean,
	viewOnly: boolean,
	dataSourceId: string,
	relateAnySubaccountEnabled: boolean
): Field[] {
	const fields: Field[] = [];
	const external = node.matchInUpsales?.isExternal || false;

	if (!viewOnly && !isSubaccount) {
		if (node.existing && Tools.FeatureHelper.isAvailable(Tools.FeatureHelper.Feature.JOURNEY_STATUS) && !external) {
			fields.push({
				type: 'journey',
				width: '35%'
			});
		} else if (dataSourceId !== 'prospecting') {
			fields.push({
				type: 'add',
				width: '35%'
			});
		} else if (node.prospectingId) {
			fields.push({
				type: 'add',
				width: '35%'
			});
		} else {
			fields.push({});
		}
	} else if (isSubaccount && (!node.prospectingId || relateAnySubaccountEnabled)) {
		fields.push({
			type: 'unlinkSubaccount',
			width: '10%'
		});
	} else {
		fields.push({});
	}

	return fields;
}
